import * as Sentry from '@sentry/nextjs'

import { safeErrorCode, safeErrorKind } from '@/lib/errors/safe-error-kind'

/**
 * 構造化 JSON ロガー
 *
 * 設計方針:
 *   - process.stdout.write でJSON 1行ずつ出力（console を使わず ESLint no-console と整合）
 *   - debug / info は stdout のみ（Vercel Runtime Logs に流れる）
 *   - warn は Sentry Logs（Sentry.logger.warn）にも送る（errors 枠は使わない・2026-09-26）
 *   - error は context.error が Error なら captureException、無ければ captureMessage('error')
 *
 * 用途:
 *   - 既存の標準出力ベースのデバッグ出力を置き換える
 *   - エラー専用の logError() (lib/errors.ts) と相互補完。
 *     logger.error は構造化ログ + Sentry、logError は Sentry 専用ヘルパー。
 *
 * 設計: docs/observability-target-state.md G3 / docs/logger-migration.md
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export type LogContext = Record<string, unknown>

interface LogEntry extends LogContext {
  level: LogLevel
  message: string
  timestamp: string
}

function emitJson(entry: LogEntry): void {
  // 2026-07-17 (PR #9974 再レビュー): 本モジュールは client component からも import される
  // ('use client' の hook が既存で 3 件)。ブラウザに process.stdout は存在せず
  // (Next.js のブラウザ process polyfill は env のみ提供し stdout を持たない)、
  // 無防備に write を呼ぶと TypeError でレンダーごと落ちる。
  // stdout の無い環境では stdout 出力だけを諦め、Sentry 送出 (warn/error) は継続する。
  if (typeof process === 'undefined' || typeof process.stdout?.write !== 'function') return
  process.stdout.write(JSON.stringify(entry) + '\n')
}

/**
 * `context.error` を、ログに出してよい形（種類名だけ）へ置き換える。
 *
 * 🔴 なぜ要るか（2026-09-13 実測・LOG-LOGGER-ERROR-OBJECT-SERIALIZE-1）
 *
 * `JSON.stringify({ error })` は、渡された値によって漏らすものが変わる。
 *
 * | 渡したもの              | 出力                                                          |
 * | ----------------------- | ------------------------------------------------------------- |
 * | 素の `Error`            | `{}`（Error のプロパティは列挙不可なので漏れない）            |
 * | **Postgres 系の例外**   | `{"detail":"Key (phone)=(090-1234-5678) already exists.",...}` |
 * | `name` を書き換えた例外 | `{"name":"山田 太郎"}`                                        |
 * | plain object            | `{"message":"…","stack":"…"}`                                 |
 *
 * **業務システムで最も多い DB 例外が、いちばん漏らす。** `detail` には一意制約に
 * 引っかかった実際の値（電話番号・メールアドレス）がそのまま入る。
 *
 * Sentry へは例外そのものを送り続ける（`sendToSentry` の第1引数）。原因調査に本文が要るため。
 *
 * 🔴 ただし「Sentry なら安全」ではない（2026-09-13 Codex 指摘・初版の記述を訂正）。
 *    `lib/sentry/pii.ts` の `beforeSend` が伏せるのは**メール・電話番号・郵便番号・URL 内の秘密**だけで、
 *    **氏名や番地は残る**。同ファイルは「Pマーク対応: 個人情報の第三者提供（米国 Sentry サーバーへの
 *    送信）を防ぐ」を目的として明記しており、そこは**まだ塞ぎ切れていない別の穴**である。
 *    ここで直したのは stdout / Vercel Runtime Logs 側だけ。Sentry 側は残課題として起票する。
 */
function sanitizeContext(context: LogContext): LogContext {
  if (!('error' in context)) return context
  const { error, ...rest } = context
  // Postgres のエラーコードは残す。種類名だけだと「権限が無い(42501)」「一意制約違反(23505)」
  // 「表が無い(42P01)」を切り分けられず、**漏らさない代わりに調べられなくなる**。
  // コードは値を含まない固定の5桁で、cause の中にいる（drizzle が包むため）。
  const code = safeErrorCode(error)
  return {
    ...rest,
    error: {
      kind: safeErrorKind(error),
      ...(code ? { code } : {}),
      // Error でない値（`error: e.message` のような既存の呼び方）で、種類だけは残す。
      // 値そのものは出さないが、`{kind:'UnknownError'}` だけだと
      // 「Error を渡し忘れている」のか「本当に未知の例外型」なのか区別できない。
      ...(error instanceof Error ? {} : { valueType: typeof error }),
    },
  }
}

function buildEntry(
  level: LogLevel,
  message: string,
  baseContext: LogContext,
  extraContext: LogContext = {}
): LogEntry {
  return {
    level,
    message,
    timestamp: new Date().toISOString(),
    ...sanitizeContext({ ...baseContext, ...extraContext }),
  }
}

function sendToSentry(level: LogLevel, message: string, context: LogContext): void {
  if (level === 'debug' || level === 'info') return

  // 🔴 extra は stdout と同じ正規化を通す。例外そのものは captureException の
  //    第1引数で渡るので、extra 側にまで生の例外を混ぜる理由が無い
  //    （extra は Sentry の issue 一覧やアラート本文に展開されて出る）。
  const safeExtra = sanitizeContext(context)

  if (level === 'warn') {
    // 2026-09-26: warning は Sentry Logs（別枠）へ。errors 枠に送らない。
    // 本番実測: errors 枠 50,000/月を warning 398,759 件（93%・1 位は 1 種で 35 万件）が食い潰し、
    // 9/22 以降は本物の error（9/26 の /customers 500 を含む）が 1 件も受信されなかった。
    // 公式: https://docs.sentry.io/platforms/javascript/guides/nextjs/logs/（Sentry.logger.warn(message, attributes)）
    // 🔴 SDK に logger が無い環境（古い版・テストの部分モック）では stdout だけに残し、errors 枠へは戻さない。
    //    公式 SDK（@sentry/nextjs 10.x）には必ず在る。部分モックのテストが 107 本ある。
    // 🔴 `?.` では防げない: Vitest の vi.mock はファクトリが返していない export に**触った時点で throw** する
    //    （"No "logger" export is defined on the mock"）。触る所ごと try で囲む。
    //    warn は記録であって業務処理の一部ではないので、ここの失敗で呼び出し元を止めない。
    try {
      const logs = (
        Sentry as { logger?: { warn?: (m: string, a?: Record<string, unknown>) => void } }
      ).logger
      logs?.warn?.(message, safeExtra)
    } catch (_sentryLogsError) {
      // 本文は呼び出し元（createLogger の warn）が stdout へ先に書いている。記録は失われない。
      // 握り潰さず「Logs へ送れなかった」ことだけ残す。logError を呼ぶと logger へ再帰するので emitJson で書く。
      // 🔴 process.stdout.write を直に呼ばない: このロガーは 'use client' の画面からも読まれ、ブラウザには
      //    stdout が無い。emitJson は stdout が無ければ黙って諦める（画面を落とさない・2026-07-17 と同じ理由）。
      emitJson({
        level: 'warn',
        message: 'logger: Sentry Logs へ送れませんでした（stdout には記録済み）',
        timestamp: new Date().toISOString(),
        errorKind: safeErrorKind(_sentryLogsError),
      })
    }
    return
  }

  // level === 'error'
  const err = context.error
  if (err instanceof Error) {
    // 例外そのものはここで渡す。原因調査には message / stack が要る。
    // 🔴 beforeSend が伏せるのはメール・電話・郵便番号・URL の秘密だけで、氏名・番地は残る。
    //    「Sentry だから安全」ではない（上の注記参照・残課題として起票済み）。
    Sentry.captureException(err, { extra: { message, ...safeExtra } })
  } else {
    Sentry.captureMessage(message, { level: 'error', extra: safeExtra })
  }
}

export interface Logger {
  debug: (message: string, context?: LogContext) => void
  info: (message: string, context?: LogContext) => void
  warn: (message: string, context?: LogContext) => void
  error: (message: string, context?: LogContext) => void
}

export function createLogger(baseContext: LogContext = {}): Logger {
  return {
    debug(message, context) {
      const entry = buildEntry('debug', message, baseContext, context)
      emitJson(entry)
    },
    info(message, context) {
      const entry = buildEntry('info', message, baseContext, context)
      emitJson(entry)
    },
    warn(message, context) {
      const entry = buildEntry('warn', message, baseContext, context)
      emitJson(entry)
      sendToSentry('warn', message, { ...baseContext, ...context })
    },
    error(message, context) {
      const entry = buildEntry('error', message, baseContext, context)
      emitJson(entry)
      sendToSentry('error', message, { ...baseContext, ...context })
    },
  }
}

/** デフォルトのトップレベル logger（追加 context なし） */
export const logger: Logger = createLogger()

/**
 * webhookの401拒否をSentryへ可視化する（2026-08-27・s08発見の型: 401はruntime error
 * として記録されずSLOに現れない。line-genba/route.tsで7日間401継続の実例あり）。
 */
export function warnWebhook401(route: string, reason: string): void {
  logger.warn(reason, { route })
}
